import { CopyOutlined } from '@ant-design/icons'
import type { FormProps } from 'antd'
import { App, Col, Form, Input, Modal, Radio, Row, Select, Typography } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { categories } from '../model/data/categories'
import { countries } from '../model/data/countries'
import { languages } from '../model/data/languages'
import { useChannels } from '../model/hooks/use-channels'
import { InternetRecourceType } from '../model/types'
import './styled.css'

const { Text } = Typography

interface AddChannelModalProps {
    open: boolean
    onClose: () => void
}

export type FieldType = {
    username: string
    language: string
    country: string
    category: string
    timezone: string
    type: InternetRecourceType
    url: string
    name: string
}

const BOT_USERNAME = process.env.BOT_USERNAME || ''

export const AddChannelModal = ({ open, onClose }: AddChannelModalProps) => {
    const {
        actions: { createChannel },
        createChannelIsLoading
    } = useChannels()

    const timezones = useMemo(() => Intl.supportedValuesOf('timeZone'), [])

    const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
        await createChannel(values)
        onClose()
    }

    const [form] = Form.useForm()

    useEffect(() => {
        form.resetFields()
    }, [form, open])

    const { message } = App.useApp()

    const [selectedResource, setSelectedResource] = useState(InternetRecourceType.channel)

    const isSelectedResourceChannel = useMemo(
        () => selectedResource === InternetRecourceType.channel,
        [selectedResource]
    )

    return (
        <Modal
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            centered
            title="Подключение интернет-ресурса"
            okText="Сохранить"
            okButtonProps={{
                loading: createChannelIsLoading
            }}
            cancelText="Отменить"
        >
            <Form form={form} name="add-channel" labelCol={{ span: 24 }} wrapperCol={{ span: 24 }} onFinish={onFinish}>
                <Row>
                    <Col span={24}>
                        <Form.Item name="type" initialValue={selectedResource}>
                            <Radio.Group onChange={(e) => setSelectedResource(e.target.value)}>
                                <Radio value="channel">Канал</Radio>
                                <Radio value="site">Сайт</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        {isSelectedResourceChannel ? (
                            <Row gutter={[16, 16]}>
                                <Col span={24}>
                                    <Text>
                                        1. Добавьте бот <a href={`https://t.me/${BOT_USERNAME}`}>@{BOT_USERNAME}</a>{' '}
                                        <CopyOutlined
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                navigator.clipboard.writeText(`@${BOT_USERNAME}`).then(() => {
                                                    message.success('Скопировано в буфер обмена')
                                                })
                                            }
                                        />{' '}
                                        в админы канала и предоставьте ему права на управление постами.
                                    </Text>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        label="2. Введите ссылку на канал или юзернейм канала в поле ниже."
                                        name="username"
                                        required={false}
                                        rules={[
                                            {
                                                required: isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label="3. Выберите категорию канала."
                                        name="category"
                                        required={false}
                                        rules={[
                                            {
                                                required: isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Выберите категорию канала"
                                        >
                                            {categories.map((c: { value: string; label: string }) => (
                                                <Select.Option key={c.value}>{c.label}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label="4. Выберите страну канала."
                                        name="country"
                                        required={false}
                                        rules={[
                                            {
                                                required: isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Выберите страну канала"
                                        >
                                            {countries.map((c: { value: string; label: string }) => (
                                                <Select.Option key={c.value}>{c.label}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label="5. Выберите язык канала."
                                        name="language"
                                        required={false}
                                        rules={[
                                            {
                                                required: isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Select showSearch style={{ width: '100%' }} placeholder="Выберите язык канала">
                                            {languages.map((c: { value: string; label: string }) => (
                                                <Select.Option key={c.value}>{c.label}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item<FieldType>
                                        label="6. Выберите часовой пояс канала."
                                        name="timezone"
                                        required={false}
                                        rules={[
                                            {
                                                required: isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Select
                                            showSearch
                                            style={{ width: '100%' }}
                                            placeholder="Выберите часовой пояс"
                                        >
                                            {timezones.map((tz: string) => (
                                                <Select.Option key={tz}>{tz}</Select.Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                        ) : (
                            <Row>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        label="Введите название сайта"
                                        name="name"
                                        required={false}
                                        rules={[
                                            {
                                                required: !isSelectedResourceChannel,
                                                message: 'Обязательное поле'
                                            }
                                        ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={24}>
                                    <Form.Item<FieldType>
                                        label="Введите ссылку на сайт"
                                        name="url"
                                        required={false}
                                        // rules={[
                                        //     {
                                        //         required: !isSelectedResourceChannel,
                                        //         message: 'Обязательное поле'
                                        //     }
                                        // ]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        )}
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}
